/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import _ from 'lodash';
import React, { memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Alert, CircularProgress, Tooltip } from '@mui/material';

import { BoxPicker } from '../../../../components/BoxPicker/BoxPicker';
import { PlanCard } from '../../../../components/PlanCard/PlanCard';
import { Namespaces } from '../../../../i18n';
import { EntityId } from '../../../../types/api/EntityId';
import { PlanCompute } from '../../../../types/common/PlanCompute';
import { PlanStorage } from '../../../../types/common/PlanStorage';
import { SystemConfiguration } from '../../../../types/superuser/SystemConfiguration';
import { fetchSystemConfiguration } from '../../../../utils/queryFns';
import { useCreateClusterFormStyles } from '../../CreateClusterOverlay.styles';
import { CreateClusterValues } from '../../CreateClusterOverlay.types';
import { PlansContext } from './consts';
import { FormProps } from './types';
import { calculateComputePlanCostPerHour, calculateStoragePlanCostPerHour } from './utils';

export const TShirtSizes = memo(<Values extends CreateClusterValues>(props: FormProps<Values>) => {
  const { setFieldValue, plan, minStorageSize = 0, values } = props;

  const { planStorage, planCompute } = plan;

  const classes = useCreateClusterFormStyles();
  const { t } = useTranslation(Namespaces.CreateCluster);

  const { selectedPlans, setSelectedPlans } = useContext(PlansContext);
  const { computeId, storageId } = selectedPlans;

  const systemConfigQuery = useQuery([fetchSystemConfiguration.key], fetchSystemConfiguration.fn);

  const systemConfig = useMemo(
    () => systemConfigQuery.data?.payload ?? ({} as SystemConfiguration),
    [systemConfigQuery.data],
  );

  const preparedComputePlans = useMemo(() => _.sortBy(planCompute, ['cpu', 'memory']), [planCompute]);

  const preparedStoragePlans = useMemo(() => _.sortBy(planStorage, ['size']), [planStorage]);

  const handleSelectComputePlan = useCallback(
    (plan: PlanCompute) => {
      setSelectedPlans({ computeId: plan.id });
      void setFieldValue('cpuUnits', plan.cpu);
      void setFieldValue('memoryGbs', plan.memory);
    },
    [setSelectedPlans, setFieldValue],
  );

  const handleSelectStoragePlan = useCallback(
    (plan: PlanStorage) => {
      setSelectedPlans({ storageId: plan.id });
      void setFieldValue('storageGbs', plan.size);
      void setFieldValue('storageClassId', plan.storageClass.id);
    },
    [setSelectedPlans, setFieldValue],
  );

  useEffect(() => {
    if (preparedComputePlans) {
      const selectedPlan = preparedComputePlans.find(
        p => p.id === computeId && p.cpu === values.cpuUnits && p.memory === values.memoryGbs,
      );
      if (!selectedPlan) {
        const defaultPlan = preparedComputePlans.find(p => p.isDefault) || preparedComputePlans[0];
        if (defaultPlan) {
          handleSelectComputePlan(defaultPlan);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (preparedStoragePlans) {
      const activePlan = preparedStoragePlans.find(p => p.id === storageId && p.size === values.storageGbs);
      if (!activePlan || activePlan.size < minStorageSize) {
        const filteredPlans = preparedStoragePlans.filter(p => p.size >= minStorageSize);
        const defaultPlan = filteredPlans.find(p => p.isDefault) || filteredPlans[0];
        defaultPlan && handleSelectStoragePlan(defaultPlan);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPlanComputeChange = (planId: EntityId) => {
    const selectedComputePlan = preparedComputePlans?.find(p => p.id === planId);
    if (selectedComputePlan) {
      handleSelectComputePlan(selectedComputePlan);
    }
  };

  const onPlanStorageChange = (planId: EntityId) => {
    const selectedStoragePlan = preparedStoragePlans?.find(p => p.id === planId);
    if (selectedStoragePlan) {
      handleSelectStoragePlan(selectedStoragePlan);
    }
  };

  const isLoading = plan.isFetching || systemConfigQuery.isLoading;

  const currentStoragePlan = useMemo(
    () => preparedStoragePlans.find(p => p.id === storageId),
    [preparedStoragePlans, storageId],
  );

  const shouldDisableComputePlan = (plan: PlanCompute) => {
    if (plan.cpu < (systemConfig.minCpuLimit ?? 0) || plan.memory < (systemConfig.minMemoryLimit ?? 0)) {
      return {
        disabled: true,
        reason: 'tiny',
      };
    } else if (
      plan.cpu > (systemConfig.maxCpuLimit ?? Number.MAX_VALUE) ||
      plan.memory > (systemConfig.maxMemoryLimit ?? Number.MAX_VALUE)
    ) {
      return {
        disabled: true,
        reason: 'large',
      };
    }
    return {
      disabled: false,
      reason: '',
    };
  };

  const shouldDisableStoragePlan = (plan: PlanStorage) => {
    if (
      currentStoragePlan &&
      plan.size < (systemConfig.minStorageLimit ?? 0) &&
      plan.storageClass.id !== currentStoragePlan.storageClass.id
    ) {
      return {
        disabled: true,
        reason: 'storage.tiny_and_different',
      };
    } else if (plan.size < (systemConfig.minStorageLimit ?? 0)) {
      return {
        disabled: true,
        reason: 'tiny',
      };
    } else if (plan.size > (systemConfig.maxStorageLimit ?? Number.MAX_VALUE)) {
      return {
        disabled: true,
        reason: 'large',
      };
    }
    return {
      disabled: false,
      reason: '',
    };
  };

  const handleComputePlanChange = (values: (string | undefined)[]) => {
    if (values[0]) {
      onPlanComputeChange(values[0]);
    }
  };

  const handleStoragePlanChange = (values: (string | undefined)[]) => {
    if (values[0]) {
      onPlanStorageChange(values[0]);
    }
  };

  const keyListener = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.code === 'Space') {
      e.preventDefault();
      const activeElement = document.activeElement;
      if (activeElement) {
        const [name, id] = activeElement.id.split('-');
        const computePlan = preparedComputePlans.find(plan => plan.name === name && plan.id === id);
        computePlan ? handleComputePlanChange([computePlan.id]) : handleStoragePlanChange([id]);
      }
    }
  };

  return (
    <div className={classes.planRoot} onKeyDown={keyListener}>
      {isLoading ? (
        <CircularProgress color="primary" size={40} />
      ) : !isLoading &&
        preparedComputePlans &&
        preparedComputePlans.length &&
        preparedStoragePlans &&
        preparedStoragePlans.length ? (
        <>
          <BoxPicker selected={[computeId]} onChange={handleComputePlanChange}>
            <div className={classes.planLabel}>{t('labels.choose_compute_plan')}</div>
            <div className={classes.planContainer}>
              {preparedComputePlans.map(planItem => {
                const disabledPlanItemData = shouldDisableComputePlan(planItem);
                const planPricing = calculateComputePlanCostPerHour(
                  planItem.cpu,
                  planItem.memory,
                  systemConfig.cpuPricePerMonth,
                  systemConfig.memoryPricePerMonth,
                );

                return (
                  <Tooltip
                    key={planItem.id}
                    title={
                      disabledPlanItemData.disabled
                        ? t(`${Namespaces.Cluster}:plan.disable_reasons.${disabledPlanItemData.reason}`)!
                        : ''
                    }
                  >
                    <div
                      className={cx(classes.planItem, {
                        [classes.disabled]: disabledPlanItemData.disabled,
                      })}
                      data-cy={`compute-plan-${planItem.name.toLowerCase()}`}
                      aria-disabled={disabledPlanItemData.disabled}
                    >
                      <BoxPicker.Item
                        value={planItem.id}
                        disabled={disabledPlanItemData.disabled}
                        id={`${planItem.name}-${planItem.id}`}
                        key={`${planItem.name}-${planItem.id}`}
                      >
                        <PlanCard
                          amount={planPricing ?? 0}
                          displayAmount={systemConfig.showInstanceCost}
                          currency={systemConfig.currency}
                          name={planItem.name}
                          type={`${t('specs.cores_spec', {
                            count: planItem.cpu,
                          })} / ${t('specs.memory_in_gb', {
                            count: planItem.memory,
                          })}`}
                          bottomTip={
                            planItem.isDefault && !disabledPlanItemData.disabled ? t('labels.recommended') : undefined
                          }
                        />
                      </BoxPicker.Item>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </BoxPicker>
          <BoxPicker selected={[storageId]} onChange={handleStoragePlanChange}>
            <div className={classes.planLabel}>{t('labels.choose_storage_plan')}</div>
            <div className={classes.planContainer}>
              {preparedStoragePlans.map(planItem => {
                const disabledPlanItemData = shouldDisableStoragePlan(planItem);
                const planPricing = calculateStoragePlanCostPerHour(planItem.size, systemConfig.storagePricePerMonth);

                return (
                  <Tooltip
                    key={planItem.id}
                    title={
                      disabledPlanItemData.disabled
                        ? t(`${Namespaces.Cluster}:plan.disable_reasons.${disabledPlanItemData.reason}`)!
                        : ''
                    }
                  >
                    <div
                      className={cx(classes.planItem, {
                        [classes.disabled]: disabledPlanItemData.disabled,
                      })}
                      data-cy={`storage-plan-${planItem.name.toLowerCase()}`}
                      aria-disabled={disabledPlanItemData.disabled}
                    >
                      <BoxPicker.Item
                        value={planItem.id}
                        disabled={disabledPlanItemData.disabled}
                        id={`${planItem.name}-${planItem.id}`}
                        key={`${planItem.name}-${planItem.id}`}
                      >
                        <PlanCard
                          amount={planPricing ?? 0}
                          displayAmount={systemConfig.showInstanceCost}
                          currency={systemConfig.currency}
                          name={planItem.name}
                          type={`${t('specs.size_in_gb', {
                            count: planItem.size,
                          })} · ${planItem.storageClass.id}`}
                          bottomTip={
                            planItem.isDefault && !disabledPlanItemData.disabled ? t('labels.recommended') : undefined
                          }
                        />
                      </BoxPicker.Item>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </BoxPicker>
        </>
      ) : (
        <Alert severity="error">{t('labels.unexpected_error')}</Alert>
      )}
    </div>
  );
});
